import {
  Box,
  Card,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { format } from 'date-fns';
import { formatCurrency } from '../../../app/utils/formatters';
import { fetchOrderDetailsAsync } from './orderDetailsSlice';
import { getOrderTypeLabel } from '../../../app/utils/orderTypeHelper';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import { OrderType } from '../../../app/data/models/orders/orderType';
import ListTitleBox from '../../../app/components/list/ListTitleBox';

const getOrderTypeChipProps = (orderType: OrderType) => {
  switch (orderType) {
    case OrderType.DineIn:
      return {
        icon: <RestaurantIcon />,
        color: 'success' as const,
        label: getOrderTypeLabel(orderType),
      };
    case OrderType.Delivery:
      return {
        icon: <LocalShippingIcon />,
        color: 'info' as const,
        label: getOrderTypeLabel(orderType),
      };
    case OrderType.TakeAway:
      return {
        icon: <TakeoutDiningIcon />,
        color: 'warning' as const,
        label: getOrderTypeLabel(orderType),
      };
  }
};

const OrderDetails = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantId, orderId } = useParams<{
    restaurantId: string;
    orderId: string;
  }>();
  const { order, status } = useAppSelector((state) => state.orderDetails);

  useEffect(() => {
    if (restaurantId && orderId) {
      dispatch(fetchOrderDetailsAsync({ restaurantId, orderId }));
    }
  }, [dispatch, restaurantId, orderId]);

  if (status.includes('pending')) return <LoadingComponent />;
  if (!order) return null;

  return (
    <>
      <ListTitleBox
        title={t('orders.details.title')}
        onBackClick={() => navigate(-1)}
      />
      <Box sx={{ pb: 2, width: '100%', margin: '0 auto' }}>
        <Grid container spacing={2}>
          {/* Order Header */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {t('orders.details.order-number')}
                    {order.tokenNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(order.orderDate), 'PPpp')}
                  </Typography>
                </Box>
                <OrderStatusLabel orderStatus={order.orderStatus} />
              </Box>
            </Card>
          </Grid>

          {/* Order Info & Kitchen Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography
                variant="h6"
                gutterBottom
                color="text.primary"
                sx={{ mb: 3 }}
              >
                {t('orders.details.order-info')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  {...getOrderTypeChipProps(order.orderType)}
                  label={t(getOrderTypeChipProps(order.orderType).label)}
                  variant="outlined"
                  sx={{
                    borderRadius: '16px',
                    '& .MuiChip-icon': {
                      color: `${getOrderTypeChipProps(order.orderType).color}.main`,
                    },
                  }}
                />
                {order.tableNumber && (
                  <Chip
                    label={`${t('orders.details.table')} ${order.tableNumber}`}
                    variant="outlined"
                    color="default"
                    sx={{ borderRadius: '16px' }}
                  />
                )}
              </Box>
              {order.notes && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 3,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('orders.details.notes')}
                  </Typography>
                  <Typography variant="body2">{order.notes}</Typography>
                </Paper>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography
                variant="h6"
                gutterBottom
                color="text.primary"
                sx={{ mb: 3 }}
              >
                {t('orders.details.kitchen-info')}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {order.subOrders.map((subOrder, index) => (
                  <Chip
                    key={subOrder.id}
                    label={subOrder.kitchenName}
                    color={index % 2 ? 'secondary' : 'info'}
                    variant="outlined"
                    sx={{ borderRadius: '16px' }}
                  />
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Order Items */}
          {order.subOrders.map((subOrder) => (
            <Grid item xs={12} key={subOrder.id}>
              <Card sx={{ p: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <Typography variant="h6" color="text.primary">
                    {subOrder.kitchenName}
                  </Typography>
                  <OrderStatusLabel orderStatus={subOrder.orderStatus} />
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {t('orders.details.item')}
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          {t('orders.details.quantity')}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                          {t('orders.details.unit-price')}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                          {t('orders.details.total')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subOrder.orderItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Box>
                              <Typography variant="body1">
                                {item.name}
                              </Typography>
                              {item.customizations?.map((customization) => (
                                <Box key={customization.id} sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                      pl: 2,
                                    }}
                                  >
                                    {customization.customerInstruction}:&nbsp;
                                    {customization.options
                                      .filter((option) => option.quantity > 0)
                                      .map((option, index, array) => (
                                        <span key={option.id}>
                                          {option.quantity}x {option.name}
                                          {option.price > 0 &&
                                            ` (+${formatCurrency(option.price)})`}
                                          {index < array.length - 1 ? ', ' : ''}
                                        </span>
                                      ))}
                                  </Typography>
                                  {customization.internalNotes && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{
                                        display: 'block',
                                        fontStyle: 'italic',
                                        mt: 0.5,
                                        pl: 2,
                                      }}
                                    >
                                      {t('orders.details.internal-note')}:{' '}
                                      {customization.internalNotes}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ verticalAlign: 'top' }}
                          >
                            {item.quantity}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ verticalAlign: 'top' }}
                          >
                            {formatCurrency(item.price)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ verticalAlign: 'top' }}
                          >
                            {formatCurrency(item.price * item.quantity)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          ))}

          {/* Order Summary */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Box display="flex" justifyContent="flex-end">
                <Table sx={{ maxWidth: 400 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body1" color="text.secondary">
                          {t('orders.details.subtotal')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(order.subtotal)}
                      </TableCell>
                    </TableRow>
                    {order.taxAmount > 0 && (
                      <TableRow>
                        <TableCell>
                          <Typography variant="body1" color="text.secondary">
                            {t('orders.details.tax')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(order.taxAmount)}
                        </TableCell>
                      </TableRow>
                    )}
                    {order.deliveryFee > 0 && (
                      <TableRow>
                        <TableCell>
                          <Typography variant="body1" color="text.secondary">
                            {t('orders.details.delivery-fee')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(order.deliveryFee)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="h6">
                          {t('orders.details.total')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none' }}>
                        <Typography variant="h6">
                          {formatCurrency(order.total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default OrderDetails;
